import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#525252] mb-4">
            Welcome Back
          </p>
          <h1 className="font-serif text-4xl text-[#F5F5F0] tracking-tight">
            Sign In
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-[#D84B79]/10 border border-[#D84B79]/30 text-[#D84B79] text-sm">
              {error}
            </div>
          )}

          <div>
            <label 
              htmlFor="email" 
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
            >
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
              placeholder="your@email.com"
              data-testid="login-email"
            />
          </div>

          <div>
            <label 
              htmlFor="password" 
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 pr-10 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
                placeholder="••••••••"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#F5F5F0] transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77] font-mono text-xs uppercase tracking-[0.2em] py-6 mt-8"
            data-testid="login-submit"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center text-[#A3A3A3] text-sm mt-8">
          Don't have an account?{' '}
          <Link 
            to="/register" 
            className="text-[#C5A059] hover:text-[#E0BE77] transition-colors"
            data-testid="login-register-link"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
